import React, { useEffect, useState } from "react";
import { Lock, Clock, ChevronDown, ShieldCheck, Loader2, CheckCircle2, AlertCircle, Download, Search, FileText } from "lucide-react";
import AppShell from "../components/app/AppShell";
import PageHeader, { EmptyState } from "../components/app/PageHeader";
import api, { formatApiError } from "../lib/api";
import { track, EVT } from "../lib/analytics";

const SEVERITY_BADGE = { info: "badge-locked", warning: "badge-pending", critical: "badge-failed" };
const EVENT_GROUPS = [
  { value: "all", label: "All events" },
  { value: "auth", label: "Authentication" },
  { value: "team", label: "User management" },
  { value: "flow", label: "Onboarding flows" },
  { value: "client", label: "Client events" },
  { value: "compliance", label: "Compliance" },
  { value: "firm", label: "Settings" },
];

export default function AuditTrail() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({ event_type: "all", severity: "all", q: "" });
  const [verification, setVerification] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: 100, offset: 0 });
      if (filters.event_type !== "all") p.set("event_type", filters.event_type);
      if (filters.severity !== "all") p.set("severity", filters.severity);
      if (filters.q) p.set("q", filters.q);
      const { data } = await api.get(`/api/audit-trail?${p}`);
      setItems(data.items); setTotal(data.total);
    } catch (e) { alert(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters.event_type, filters.severity]);

  const verify = async () => {
    setVerifying(true);
    try { const { data } = await api.post("/api/audit-trail/verify-chain"); setVerification(data); }
    catch (e) { alert(formatApiError(e)); }
    finally { setVerifying(false); }
  };

  // Streams the filtered export back through the browser. Uses fetch (not axios)
  // so we can pull the response as a blob and save it without buffering through
  // JS strings — important for very large audit logs.
  const [exporting, setExporting] = useState({ csv: false, pdf: false });
  const doExport = async (kind) => {
    setExporting((s) => ({ ...s, [kind]: true }));
    try {
      const p = new URLSearchParams();
      if (filters.event_type !== "all") p.set("event_type", filters.event_type);
      if (filters.severity !== "all") p.set("severity", filters.severity);
      if (filters.q) p.set("q", filters.q);
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${base}/api/audit-trail/export.${kind}?${p}`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = res.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1] || `audit.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      track(EVT.audit_trail_exported, { format: kind });
    } catch (e) { alert("Export failed: " + (e.message || e)); }
    finally { setExporting((s) => ({ ...s, [kind]: false })); }
  };

  return (
    <AppShell>
      <PageHeader
        title="FCA Compliance Audit Trail"
        subtitle={`${total.toLocaleString()} events on record · 7-year retention`}
        actions={
          <div className="flex flex-wrap gap-2">
            <button onClick={() => doExport("csv")} disabled={exporting.csv} className="btn btn-secondary" data-testid="audit-export-csv">
              {exporting.csv ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {exporting.csv ? "Exporting…" : "Export CSV"}
            </button>
            <button onClick={() => doExport("pdf")} disabled={exporting.pdf} className="btn btn-secondary" data-testid="audit-export-pdf">
              {exporting.pdf ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {exporting.pdf ? "Exporting…" : "Export PDF"}
            </button>
          </div>
        }
      />

      <div className="security-card p-4 mb-6 flex items-start gap-3" data-testid="audit-immutable-banner">
        <Lock size={18} className="text-emerald2-dark mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="t-body-sm font-semibold">All records are cryptographically sealed via SHA-256 hash chain</div>
          <p className="t-caption">They cannot be modified or deleted by anyone — including platform administrators. Retention: 7 years.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card2 p-4 mb-5 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate2" />
          <input className="input2 pl-9" placeholder="Search descriptions…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <select className="input2 md:w-52" value={filters.event_type} onChange={(e) => setFilters({ ...filters, event_type: e.target.value })} data-testid="filter-type">
          {EVENT_GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
        </select>
        <select className="input2 md:w-40" value={filters.severity} onChange={(e) => setFilters({ ...filters, severity: e.target.value })} data-testid="filter-severity">
          <option value="all">All severities</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="critical">Critical</option>
        </select>
      </div>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-14" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Clock} title="No events match your filters" description="Try clearing filters or running an action in another tab." />
      ) : (
        <div className="card2 overflow-hidden">
          <table className="w-full">
            <thead className="bg-surface border-b border-border2 text-left"><tr>
              <th className="px-5 py-3 t-label text-slate2">Time</th>
              <th className="px-5 py-3 t-label text-slate2">Event</th>
              <th className="px-5 py-3 t-label text-slate2">User</th>
              <th className="px-5 py-3 t-label text-slate2">Severity</th>
              <th className="px-5 py-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-border2">
              {items.map((e) => (
                <React.Fragment key={e.event_id}>
                  <tr className="hover:bg-surface cursor-pointer" onClick={() => setExpanded(expanded === e.event_id ? null : e.event_id)} data-testid={`audit-row-${e.event_id}`}>
                    <td className="px-5 py-3 t-caption t-mono">{new Date(e.timestamp).toLocaleString()}</td>
                    <td className="px-5 py-3 t-body-sm">{e.action}</td>
                    <td className="px-5 py-3 t-body-sm">{e.user_email || "system"}</td>
                    <td className="px-5 py-3"><span className={`badge2 ${SEVERITY_BADGE[e.severity] || "badge-draft"}`}>{e.severity}</span></td>
                    <td className="px-5 py-3 text-right"><ChevronDown size={14} className={`text-slate2 transition-transform ${expanded === e.event_id ? "rotate-180" : ""}`} /></td>
                  </tr>
                  {expanded === e.event_id && (
                    <tr><td colSpan={5} className="px-5 py-4 bg-surface">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 t-caption">
                        <div><div className="t-label">Event ID</div><div className="t-mono break-all">{e.event_id}</div></div>
                        <div><div className="t-label">Type</div><div className="t-mono">{e.event_type}</div></div>
                        <div><div className="t-label">IP (masked)</div><div className="t-mono">{e.ip_address || "—"}</div></div>
                        <div><div className="t-label">Session</div><div className="t-mono break-all">{e.session_id || "—"}</div></div>
                        <div><div className="t-label">Hash</div><div className="t-mono break-all">{e.hash?.slice(0, 16)}…</div></div>
                        <div><div className="t-label">Prev hash</div><div className="t-mono break-all">{e.prev_hash?.slice(0, 16) || "—"}…</div></div>
                        {e.metadata && Object.keys(e.metadata).length > 0 && (
                          <div className="col-span-2"><div className="t-label">Metadata</div><pre className="t-mono text-xs bg-white border border-border2 p-2 rounded-md overflow-x-auto">{JSON.stringify(e.metadata, null, 2)}</pre></div>
                        )}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Integrity verification */}
      <div className="card2 p-6 mt-8" data-testid="integrity-verification">
        <h3 className="t-h3 mb-2 flex items-center gap-2"><ShieldCheck size={20} className="text-emerald2-dark" />Verify the integrity of your audit trail</h3>
        <p className="t-body-sm text-slate2 mb-4">Runs a SHA-256 hash chain verification across every event in your firm's audit trail. Should always come back clean — if it doesn't, contact security@compliche.com immediately.</p>
        {verification && (
          <div className={`p-4 mb-4 rounded-md ${verification.valid ? "bg-emerald2-50 text-emerald2-700" : "bg-rose2-50 text-rose2-700"}`} data-testid="verification-result">
            {verification.valid ? (
              <div className="flex items-center gap-2"><CheckCircle2 size={20} />All {verification.checked.toLocaleString()} records verified — chain intact. Last checked: {new Date(verification.verified_at).toLocaleString()}</div>
            ) : (
              <div className="flex items-center gap-2"><AlertCircle size={20} />Chain integrity issue at record {verification.broken_at}. Contact support immediately.</div>
            )}
          </div>
        )}
        <button onClick={verify} disabled={verifying} className="btn btn-primary" data-testid="verify-chain">
          {verifying ? <><Loader2 size={14} className="animate-spin" />Verifying chain…</> : <><ShieldCheck size={14} />Run integrity verification</>}
        </button>
      </div>
    </AppShell>
  );
}
